import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '@/assets/app/css/mypage.module.css';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import useAppStore from '@/shared/store/UseAppStore';
import ImgHeaderLevel from '@/assets/app/images/common/header/header-level.png';
import ImgHeaderStar from '@/assets/app/images/common/header/header-star.png';
import ImgHeaderCalendar from '@/assets/app/images/common/header/header-calendar.png';
import defaultPersonImage from '@/assets/app/images/common/default-person.svg';
import mypageBg from '@/assets/app/images/mypage/mypage_bg.png';
import ImgArrow from '@/assets/app/images/common/header/arrow.png';
import IconLeaf from '@/assets/app/images/icon/icon-leaf.png';
import IconPen from '@/assets/app/images/icon/icon-Pen.png';
import { getUploadsUrl, openUpdateNicknameSheet, vAppAjax } from '@/shared/utils/CommonUtil';
import { motion } from 'framer-motion';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';
import logger from '@/shared/utils/Logger';
import TitleHeader from '../common/TitleHeader';

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { setIsShowBottomNavigation, setIsShowProfileHeader } = useAppStore();
  const [data, setData] = useState<CompletedInfo>();
  const { member } = useAppAuthStore();

  const loadData = async () => {
    await vAppAjax(
      'get',
      `my/recent-achievement`,
      {},
      (response: AxiosResponse<CommonResult<CompletedInfo>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setData(response.data.result);
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  useEffect(() => {
    setIsShowBottomNavigation(true);
    setIsShowProfileHeader(false);
    loadData();
  }, []);

  return (
    <div className={`page-container page-container--gray ${styles.mypage}`}>
      <TitleHeader title="내정보" onBack={() => navigate(-1)}>
        {/* TitleHeader 내부의 children 슬롯으로 쏙 들어갑니다 */}
        <div className="font-bold cursor-pointer" onClick={() => navigate('/mypage/settings')}>
          <span style={{ color: 'rgb(20, 161, 200)' }}>설정</span>
        </div>
      </TitleHeader>

      <main className="relative px-4 items-start justify-center">
        <div
          className="w-full relative rounded-[14px] overflow-hidden"
          style={{
            backgroundImage: `url(${mypageBg})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '309 / 381',
          }}
        >
          <div className="w-full px-[25px] mt-4">
            <div className="flex justify-between gap-[16px]">
              <div className="flex-1 flex flex-col items-center justify-center h-[96px] border border-[#E5E5E5] rounded-[14px] bg-white">
                <img src={ImgHeaderLevel} alt="level" className="mb-1" />
                <span className="text-[16px] font-extrabold">Lv.{member?.level}</span>
                <span className="text-[11px] font-normal text-[#8D8D8D]">나의 레벨</span>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center h-[96px] border border-[#E5E5E5] rounded-[14px] bg-white">
                <img src={ImgHeaderStar} alt="points" className="mb-1" />
                <span className="text-[16px] font-extrabold">{member?.totalPoints}</span>
                <span className="text-[11px] font-normal text-[#8D8D8D]">별포인트</span>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center h-[96px] border border-[#E5E5E5] rounded-[14px] bg-white">
                <img src={ImgHeaderCalendar} alt="calendar" className="mb-1" />
                <div className="text-[16px] font-extrabold flex items-baseline">
                  {member?.consecutiveDays}
                  <span className="text-[12px] font-normal ml-0.5">일차</span>
                </div>
                <span className="text-[11px] font-normal text-[#8D8D8D]">연속학습</span>
              </div>
            </div>
          </div>

          <div
            className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center z-10"
            style={{ bottom: '18%' }} // ✅ 배경 하단에서 50px 띄움
          >
            <img
              src={`${getUploadsUrl()}${member?.levelImageFilename.replace('/uploads', '')}`}
              className="object-contain"
            />
          </div>

          {/* 바닥 언덕 (CSS로 구현하거나 이미지 처리) */}
        </div>
        <div
          className="relative flex items-center mb-4 bg-[#0094EF]"
          style={{
            borderBottom: '4px solid #0070D6',
            overflow: 'visible',
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginTop: -14,
          }}
        >
          <div className="relative w-22 h-22 -mt-12 shrink-0">
            {/* 1. 프로필 이미지: 부모 크기(w-full h-full)를 100% 채우도록 설정 */}
            <img
              src={
                member?.profileImageFilename
                  ? `${getUploadsUrl()}/character/${member?.profileImageFilename}`
                  : defaultPersonImage
              }
              alt={member?.nickname}
              className="w-full h-full rounded-full object-cover border-0"
            />

            {/* 2. 펜 아이콘: 우측 하단에 고정 */}
            <div
              className="absolute bottom-0 right-0 w-6 h-6 bg-white border border-[#EBEBEB] flex items-center justify-center rounded-full cursor-pointer shadow-sm"
              onClick={() => navigate('character')}
            >
              {/* 펜 아이콘 크기가 너무 크다면 w-3 h-3 등으로 조절하세요 */}
              <img src={IconPen} className="w-3 h-3 object-contain" alt="edit" />
            </div>
          </div>
          <div className="w-full" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', color: '#fff' }}>
              <span className="text-xl f-one-pop font-bold font-[800]">{member?.nickname}</span>
              <span className="text-[11px]">
                가입일: {new Date(member?.createdAt || '').toLocaleDateString('ko-KR')}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <button
                className="bg-white f-one-pop text-[#0094EF]"
                style={{
                  borderRadius: 25,
                  paddingTop: 6,
                  paddingBottom: 2,
                  fontSize: 13,
                  width: 60,
                  borderBottom: '4px solid #B9D3EF',
                }}
                onClick={openUpdateNicknameSheet}
              >
                수정하기
              </button>
            </div>
          </div>
        </div>
      </main>

      <div className="section" style={{ margin: '15px 25px' }}>
        <div className="section-header">
          <h3 className="section-header__title" style={{ fontSize: 18, fontWeight: 800 }}>
            나의 업적
          </h3>
          <button
            className="bg-[#F3A9C9] f-one-pop text-white"
            style={{
              borderRadius: 25,
              paddingTop: 6,
              paddingBottom: 2,
              fontSize: 13,
              width: 60,
              borderBottom: '4px solid #EF73AE',
            }}
            onClick={() => navigate('/mypage/achievements')}
          >
            전체보기
          </button>
        </div>

        {data?.personName && (
          <div
            className={`flex items-center justify-center gap-3 mb-2 mt-4 theme-${data.themeColor}`}
          >
            <span className="px-3 py-0.5 border border-[#F3A9C9] text-[#F3A9C9] text-xs font-bold rounded-full">
              최근
            </span>
            <span className="text-sm font-medium" style={{ color: 'var(--color-btn)' }}>
              {data.categoryName} · {data.personName} · {data.chapterTitle}
            </span>
          </div>
        )}

        <div className="flex items-center justify-center border border-white f-one-pop bg-[#F3A9C9] text-white rounded-[50px] w-100p py-3 mt-3 mx-auto">
          <div className="flex-1 text-center">
            <div className="text-[24px] font-extrabold">
              {data?.completedPerson || 0} / {data?.totalPerson}
            </div>
            <div className="text-[14px]">완료한 인물</div>
          </div>

          <div className="w-[0.5px] h-[42px] bg-[#efefef]"></div>

          <div className="flex-1 text-center">
            <div className="text-[24px] font-extrabold ">
              {data?.completedEra || 0} / {data?.totalEra}
            </div>
            <div className="text-[14px]">완료한 시대</div>
          </div>
        </div>

        {/* {data?.achievements.map((item, idx) => (
          <div
            key={idx}
            className={`bg-white py-4 flex items-center gap-4 theme-${item.themeColor}`}
          >
            <div
              className={`w-20 px-2 py-2 rounded-[25px] flex items-center justify-center text-white font-bold text-sm shrink-0`}
              style={{ background: `var(--color-btn)` }}
            >
              {item.categoryName.replace('시대', '')}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-[16px] bg-[#DEF0FB] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(item.completedChapters / item.totalChapters) * 100}%` }}
                    className="h-full rounded-lg"
                    style={{
                      backgroundColor:
                        item.completedChapters === item.totalChapters ? '#F3A9C9' : '#0094EF',
                    }}
                  />
                </div>
                <span className="text-xs font-bold" style={{ color: '#0094EF' }}>
                  {item.completedChapters}/{item.totalChapters}
                </span>
              </div>
            </div>
          </div>
        ))} */}
      </div>
    </div>
  );
};

export default MyPage;
